import { DOMAIN_REGEX, WEBSITE_URL_REGEX } from '@mondaygpt/constants'

/** Strip protocol, www prefix, and path from a URL to get the bare domain. Display only. */
export function cleanDomain(value: string): string {
  return value
    .trim()
    .replace(/^(https?:\/\/)?(www\.)?/, '')
    .replace(/\/.*$/, '')
}

/** Strip protocol and www, but keep the path. Use when sending to server. */
export function cleanUrl(value: string): string {
  return value
    .trim()
    .replace(/^(https?:\/\/)?(www\.)?/, '')
    .replace(/\/+$/, '')
}

/** Returns true if the value is a valid domain (empty string returns false). */
export function isValidDomain(value: string): boolean {
  if (!value.trim()) return false
  return DOMAIN_REGEX.test(cleanDomain(value))
}

/** Returns true if the value is a valid URL (domain with optional path). */
export function isValidUrl(value: string): boolean {
  if (!value.trim()) return false
  return WEBSITE_URL_REGEX.test(cleanUrl(value))
}

/**
 * Extract a social handle from user input.
 * Accepts plain handles ("nike", "@nike") or full URLs
 * ("https://instagram.com/nike", "https://www.facebook.com/nike/").
 */
export function extractSocialHandle(input: string, platform: 'instagram' | 'facebook'): string {
  const trimmed = input.trim()
  if (!trimmed) return ''

  // Facebook share URLs (facebook.com/share/1EEkvZDvHe/) — pass through for server resolution
  if (platform === 'facebook') {
    const shareMatch = trimmed.match(/(?:facebook\.com|fb\.com)\/share\/([a-zA-Z0-9]+)/i)
    if (shareMatch) return `share/${shareMatch[1]}`
    // Modern Page URL: /profile.php?id=<numericId>. The id is the handle — the
    // generic regex below would collapse this to a useless `profile.php`.
    const profileMatch = trimmed.match(
      /(?:facebook\.com|fb\.com)\/profile\.php\?(?:[^#]*&)?id=(\d+)/i
    )
    if (profileMatch) return `profile.php?id=${profileMatch[1]}`
  }

  const patterns =
    platform === 'instagram'
      ? [/instagram\.com\/([^/?#]+)/i]
      : [/facebook\.com\/([^/?#]+)/i, /fb\.com\/([^/?#]+)/i]

  for (const pattern of patterns) {
    const match = trimmed.match(pattern)
    if (match) {
      const handle = match[1].replace(/\/+$/, '')
      // Filter out generic FB pages. `profile.php` without a usable ?id= (handled
      // above) is a bare login/redirect page, not a real handle.
      if (
        ['share', 'sharer', 'dialog', 'login', 'groups', 'pages', 'profile.php'].includes(
          handle.toLowerCase()
        )
      ) {
        return ''
      }
      return handle
    }
  }

  // Plain handle — strip leading @ and remove invalid characters
  const handle = trimmed.replace(/^@/, '')
  if (platform === 'instagram') {
    return handle.replace(/[^a-zA-Z0-9._]/g, '')
  }
  return handle
}
